import * as Dialog from '@radix-ui/react-dialog';
import { ReactNode } from 'react';

import { Button } from '@/components/Button/Button';
import { RegularXmark } from '@/icons/components';
import { createScopedContext } from '@/utils/createScopedContext/createScopedContext';
import { twJoin } from '@/utils/tw-merge';

import { Warning } from '../../Warning';
import {
  ModalBuilder,
  ModalBuilderRootProps,
  useModalBuilderContext,
} from '../builder/ModalBuilder';
import { ModalStateProps } from '../manager/useModalState';

export type ModalProps = ModalBuilderRootProps & {
  size?: 'medium' | 'large' | 'xlarge';
  customWidth?: `max-w-${string}`;
  addContentPadding?: boolean;
  beforeClose?: () => void;
  modalStateProps?: ModalStateProps;
};

const [ModalDefaultProvider, useModalDefaultContext] =
  createScopedContext<Pick<ModalProps, 'addContentPadding'>>();

export function Modal({
  children,
  title,
  description,
  addContentPadding = true,
  size = 'medium',
  customWidth,
  ...props
}: ModalProps) {
  return (
    <ModalBuilder.Root title={title} description={description} {...props}>
      <ModalDefaultProvider addContentPadding={addContentPadding}>
        <DialogContent
          size={size}
          title={title}
          customWidth={customWidth}
          addContentPadding={addContentPadding}
          description={description}
        >
          {children}
        </DialogContent>
      </ModalDefaultProvider>
    </ModalBuilder.Root>
  );
}

const DialogContent = ({
  title,
  addContentPadding,
  description,
  children,
  customWidth,
  size = 'large',
}: Pick<
  ModalProps,
  | 'size'
  | 'title'
  | 'addContentPadding'
  | 'description'
  | 'children'
  | 'customWidth'
>) => {
  const { modalStateProps } = useModalBuilderContext();

  return (
    <ModalBuilder.Content size={size} customWidth={customWidth}>
      <div className="flex shrink-0 grow-0 items-center justify-between border-b border-b-gray-300 px-3 py-2">
        <Dialog.Title className="style-h5-bold text-gray-800">
          {title}
        </Dialog.Title>
        <Dialog.Close asChild>
          <Button
            variant="tertiary"
            size="icon"
            icon={RegularXmark}
            disabled={modalStateProps?.action?.type === 'pending'}
          />
        </Dialog.Close>
      </div>

      <div
        className={twJoin(
          'grow-2 overflow-y-auto px-3',
          addContentPadding && 'pt-3'
        )}
      >
        {description && (
          <Dialog.Description className="style-body text-black" asChild>
            {description}
          </Dialog.Description>
        )}

        {children}
      </div>
    </ModalBuilder.Content>
  );
};

Modal.Footer = function ModalFooter({
  left,
  close,
  action: actionSlot,
}: {
  left?: ReactNode;
  close?: ReactNode;
  action?: ReactNode;
}) {
  const { modalStateProps: { action } = {} } = useModalBuilderContext();
  const { addContentPadding } = useModalDefaultContext();

  return (
    <>
      {action?.type === 'error' && (
        <Warning size="large" variant="error" className="mt-2">
          <Warning.Title asChild>
            <h5>{action.title}</h5>
          </Warning.Title>
          <Warning.Description>{action.description}</Warning.Description>
        </Warning>
      )}

      <div
        className={twJoin(
          'sticky bottom-0 left-0 gap-1 border-t border-t-gray-300 bg-white py-2',
          addContentPadding && 'mt-2',
          'flex items-center justify-end'
        )}
      >
        {left && <div className="grow-2 w-full">{left}</div>}

        {close}
        {actionSlot}
      </div>
    </>
  );
};

Modal.Action = ModalBuilder.Action;

Modal.Close = function ModalClose({
  disabled,
  label = 'Annuleren',
}: {
  disabled?: boolean;
  label?: string;
}) {
  const { modalStateProps } = useModalBuilderContext();

  return (
    <Dialog.Close asChild>
      <Button
        variant="secondary"
        disabled={disabled ?? modalStateProps?.action?.type === 'pending'}
      >
        {label}
      </Button>
    </Dialog.Close>
  );
};
